import Linkify from 'linkify-react';
import { Fragment } from 'react';

import { toTextRuns, unwrapConventions } from './email-text-conventions';
import type { TextRun } from './email-text-conventions';

/**
 * An email's text part, made readable.
 *
 * The feed renders text rather than HTML, which leaves the flattener's
 * conventions on show. `email-text-conventions.ts` undoes the ones we can read;
 * this puts what is left on the page and makes its links clickable. Linkify
 * emits React elements rather than markup, so nothing here reaches the DOM as
 * HTML and the feed needs no sanitizer of its own.
 */
export function EmailTextContent({ text }: { text: string }) {
  return (
    <Linkify
      as="p"
      className="whitespace-pre-wrap wrap-anywhere text-sm leading-5"
      options={{
        className: 'underline underline-offset-2',
        target: '_blank',
        rel: 'noopener noreferrer',
        // A tracking URL runs past 200 characters and would otherwise be the
        // longest thing on the page. Only the label is cut; the href stays whole.
        format: (value) =>
          value.length > URL_LABEL_MAX_LENGTH
            ? `${value.slice(0, URL_LABEL_MAX_LENGTH)}…`
            : value,
      }}
    >
      {toTextRuns(unwrapConventions(text)).map(renderRun)}
    </Linkify>
  );
}

function renderRun(run: TextRun, index: number) {
  if (run.isBold) {
    return <strong key={index}>{run.text}</strong>;
  }

  return <Fragment key={index}>{run.text}</Fragment>;
}

const URL_LABEL_MAX_LENGTH = 48;
